import { Formik } from "formik";
import * as Yup from "yup";
import { COUNTRIES } from "./constants/countries";
import { FormCreatePersonalSpaceBody } from "./FormCreatePersonalSpaceBody";
import axios from "axios";
import { getRequestHeaders } from "utils/getRequestHeaders";
import { captureException } from "@sentry/react";
import { getEnvVar } from "ideas.env";
import { EuiLoadingChart, EuiPageTemplate } from "@inscopix/ideas-eui";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "django/queryKeys";
import { useCallback, useMemo } from "react";
import { getFieldsByPage } from "./FormCreatePersonalSpace.helpers";
import { CreatePersonalSpaceError } from "pages/create-personal-space/content/CreatePersonalSpaceError";

export const PERSONAL_SPACE_FORM_OCCUPATION_OPTIONS = [
  { key: "occupation_undergrad", display: "Undergraduate Student" },
  { key: "occupation_technician", display: "Research Technician" },
  { key: "occupation_graduate_student", display: "Graduate Student" },
  { key: "occupation_postdoc", display: "Postdoctoral Researcher" },
  { key: "occupation_pi", display: "PI" },
  { key: "occupation_working_in_industry", display: "Working in industry" },
  { key: "occupation_other", display: "Other (Please specify)" },
] as const;

export const PERSONAL_SPACE_FORM_DATA_TYPE_OPTIONS = [
  { key: "data_type_1p_miniscopes", display: "1P Miniscopes" },
  { key: "data_type_2p_miniscopes", display: "2P Miniscopes" },
  {
    key: "data_type_2p_benchtop_microscopes",
    display: "2P Benchtop Microscopes",
  },
  { key: "data_type_behavior", display: "Behavior" },
  { key: "data_type_other", display: "Other (Please specify)" },
] as const;

export const PERSONAL_SPACE_FORM_ANALYTICS_OPTIONS = [
  { key: "analytics_calcium_imaging", display: "Calcium Imaging" },
  { key: "analytics_photometry", display: "Photometry" },
  {
    key: "analytics_neurotransmitter_imaging",
    display: "Neurotransmitter Imaging",
  },
  { key: "analytics_blood_flow_imaging", display: "Blood Flow Imaging" },
  {
    key: "analytics_behavior_pose_estimation",
    display: "Behavior Pose Estimation and/or Locomotion",
  },
  {
    key: "analytics_analyzing_activity_over_time",
    display: "Analyzing Neural Activity Over Time",
  },
  {
    key: "analytics_analyzing_activity_across_states",
    display: "Analyzing Neural Activity Across States or Timestamped Events",
  },
  { key: "analytics_other", display: "Other (Please specify)" },
] as const;

export const PERSONAL_SPACE_FORM_INTEREST_OPTIONS = [
  {
    key: "interest_purchasing",
    display: "Purchasing - Active in the buying process",
  },
  { key: "interest_investing", display: "Investing - Buying within a year" },
  {
    key: "interest_searching",
    display: "Searching - Investigating technologies for my application",
  },
  { key: "interest_learning", display: "Learning - Expanding my knowledge" },
] as const;

export const PERSONAL_SPACE_FORM_FIELD_NAMES_BY_KEY: Record<
  keyof PersonalSpaceFormFields,
  string
> = {
  country: "Where are you located?",
  state: "Which state/province do you live in?",
  occupation: "What is your role?",
  occupationDetail: "Please specify your role",
  dataType:
    "What kinds of data would you like to use on the IDEAS platform? (optional)",
  analytics:
    "What kinds of analytics would you like to do on the IDEAS platform? (optional)",
  analyticsDetail: "Please specify other analytics",
  dataTypeDetail: "Please specify other data types",
  institution: "What institution or company are you affiliated with?",
  group: "What laboratory or group are you affiliated with? (optional)",
  marketingConsent:
    "We'd like to keep in touch and send some information to help get you started! Check here if you agree to be contacted by Inscopix and Bruker about IDEAS and other related products.",
  interest: "What best describes your current interest in IDEAS?",
};

export const formCreatePersonalSpaceValidationSchema = Yup.object({
  occupation: Yup.string()
    .required("Please select a role")
    // // workaround for empty initial value
    .oneOf(
      PERSONAL_SPACE_FORM_OCCUPATION_OPTIONS.map((option) => option.key),
      "Please select a role",
    ),

  country: Yup.string()
    .required("Please select a country")
    .oneOf(COUNTRIES.map(({ CODE }) => CODE)),
  state: Yup.string()
    .when("country", {
      is: "US" as const satisfies (typeof COUNTRIES)[number]["CODE"],
      then: (schema) => schema.required("Please specify your state"),
    })
    .when("country", {
      is: "CA" as const satisfies (typeof COUNTRIES)[number]["CODE"],
      then: (schema) => schema.required("Please specify your province"),
    }),
  occupationDetail: Yup.string()
    .max(255)
    .when("occupation", {
      is: "occupation_other" as const satisfies (typeof PERSONAL_SPACE_FORM_OCCUPATION_OPTIONS)[number]["key"],
      then: (schema) => schema.required("Please select your role"),
    }),
  institution: Yup.string()
    .required("Please specify a company or institution")
    .max(255)
    .min(2, "Please specify a company or institution"),
  group: Yup.string().max(255),
  dataType: Yup.array().of(
    Yup.string().oneOf(
      PERSONAL_SPACE_FORM_DATA_TYPE_OPTIONS.map((option) => option.key),
    ),
  ),
  dataTypeDetail: Yup.string().max(255),
  analytics: Yup.array().of(
    Yup.string().oneOf(
      PERSONAL_SPACE_FORM_ANALYTICS_OPTIONS.map((option) => option.key),
    ),
  ),
  analyticsDetail: Yup.string().max(255),
  marketingConsent: Yup.boolean(),
  interest: Yup.string()
    .required("Please select an option")
    .oneOf(
      PERSONAL_SPACE_FORM_INTEREST_OPTIONS.map((option) => option.key),
      "Please select an option",
    ),
});

export type PersonalSpaceFormFields = Yup.InferType<
  typeof formCreatePersonalSpaceValidationSchema
>;

export type FormCreatePersonalSpaceState =
  | "questions"
  | "submitting"
  | "failure"
  | "success";
export interface FormCreatePersonalSpaceProps {
  onComplete: () => Promise<void>;
  formState: FormCreatePersonalSpaceState;
  setFormState: (state: FormCreatePersonalSpaceState) => void;
  marketingConsentData: boolean | undefined;
}

export const FormCreatePersonalSpace = ({
  onComplete,
  formState,
  setFormState,
  marketingConsentData,
}: FormCreatePersonalSpaceProps) => {
  const queryClient = useQueryClient();

  const fieldsByPage = useMemo(
    () =>
      getFieldsByPage({
        initialMarketingConsent: marketingConsentData ?? false,
      }),
    [marketingConsentData],
  );

  const handleSubmit = useCallback(
    async (values: PersonalSpaceFormFields) => {
      setFormState("submitting");
      let onboardedSuccess = false;

      if (!onboardedSuccess) {
        const data = {
          institution: values.institution,
          country: values.country,
          state: values.state,
          occupation: values.occupation,
          occupation_detail: values.occupationDetail,
          data_type: values.dataType,
          data_type_detail: values.dataTypeDetail,
          analytics: values.analytics,
          analytics_detail: values.analyticsDetail,
          group: values.group,
          email_marketing_consent: values.marketingConsent,
          interest: values.interest,
        };

        for (const key in data) {
          const val = data[key as keyof typeof data];
          if (val === "") {
            delete data[key as keyof typeof data];
          }
        }

        try {
          const headers = await getRequestHeaders();
          const res = await axios.post(getEnvVar("URL_ONBOARD"), data, {
            headers,
          });

          if (res.status !== 201) {
            throw new Error("Failed to answer onboarding questions");
          }
          await queryClient.invalidateQueries({
            queryKey: queryKeys.onboarded,
          });
          onboardedSuccess = true;
        } catch (error) {
          captureException(error);
        }
      }

      if (!onboardedSuccess) {
        setFormState("failure");
        return;
      }
      setFormState("success");
      void onComplete();
    },
    [onComplete, queryClient, setFormState],
  );

  if (formState === "failure") {
    return <CreatePersonalSpaceError />;
  }

  if (formState === "submitting") {
    return (
      <EuiPageTemplate.EmptyPrompt
        title={<span>Submitting your request...</span>}
        icon={<EuiLoadingChart size="xl" mono />}
      >
        This can take a few minutes.
      </EuiPageTemplate.EmptyPrompt>
    );
  }

  return (
    <EuiPageTemplate.Section>
      <Formik<PersonalSpaceFormFields>
        onSubmit={async (values) => {
          await handleSubmit(values);
        }}
        initialValues={{
          institution: "",
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          country: "",
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          occupation: "",
          occupationDetail: "",
          dataTypeDetail: "",
          dataType: [],
          group: "",
          marketingConsent: marketingConsentData ?? false,
        }}
        validationSchema={formCreatePersonalSpaceValidationSchema}
        validateOnMount
      >
        <FormCreatePersonalSpaceBody fieldsByPage={fieldsByPage} />
      </Formik>
    </EuiPageTemplate.Section>
  );
};
